import React, { useRef, useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * DateInput — always displays/enters DD/MM/YYYY regardless of OS locale.
 *
 * The user can TYPE the date directly (critical for birthdates, where native
 * calendar pickers open on the current month and make reaching a birth year
 * painful, and where `showPicker()` is unreliable on iOS Safari). A calendar
 * button opens the native picker as an optional enhancement.
 *
 * value/onChange use yyyy-MM-dd (same as a native date input). onChange is
 * called with a synthetic `{ target: { value } }` so existing callers that
 * read `e.target.value` keep working unchanged.
 */
function isoToDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
}

// DD/MM/YYYY (or any string with 8 digits) -> yyyy-MM-dd, or null if not a
// complete, real calendar date.
function displayToIso(display) {
  const digits = (display || '').replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(yyyy);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1) return null;
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null; // rejects 31/02, 31/04, etc.
  }
  return `${yyyy}-${mm}-${dd}`;
}

// Progressively insert slashes as the user types: 07031985 -> 07/03/1985
function formatDigits(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 8);
  let out = d.slice(0, 2);
  if (d.length >= 3) out += '/' + d.slice(2, 4);
  if (d.length >= 5) out += '/' + d.slice(4, 8);
  return out;
}

export function DateInput({ value, onChange, className, min, max, disabled, ...props }) {
  const hiddenRef = useRef(null);
  const [text, setText] = useState(() => isoToDisplay(value));

  // Reflect external value changes (picker selection, reset, async load) into
  // the text field — but don't clobber an in-progress partial that already
  // resolves to the same value.
  useEffect(() => {
    if ((displayToIso(text) || '') !== (value || '')) {
      setText(isoToDisplay(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleText = (e) => {
    const formatted = formatDigits(e.target.value);
    setText(formatted);
    const iso = displayToIso(formatted);
    if (iso) {
      onChange?.({ target: { value: iso } });
    } else if (formatted === '') {
      onChange?.({ target: { value: '' } });
    }
  };

  const openPicker = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const el = hiddenRef.current;
    if (!el || disabled) return;
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
        return;
      }
    } catch (_) {
      // showPicker can throw without a user gesture — fall through.
    }
    el.focus();
    el.click();
  };

  const { 'data-testid': testId, ...restProps } = props;

  return (
    <div className="relative">
      {/* Typeable DD/MM/YYYY field — the real input. */}
      <input
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/YYYY"
        value={text}
        onChange={handleText}
        disabled={disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background',
          'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        data-testid={testId}
        {...restProps}
      />
      {/* Calendar button opens the native picker as an enhancement. */}
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open calendar"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        <Calendar className="h-4 w-4" />
      </button>
      {/* Hidden native date input — backs the calendar picker only. */}
      <input
        ref={hiddenRef}
        type="date"
        value={value || ''}
        onChange={onChange}
        min={min}
        max={max}
        disabled={disabled}
        className="absolute inset-0 h-0 w-0 opacity-0 pointer-events-none"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}
